import React from 'react'
import { StyledWorkPreferenceForm } from './styled'
import { API } from 'javascripts/api'
import { FormProvider, useForm } from 'react-hook-form'
import { WorkPreference, WorkPreferenceFormFormGroupEnum } from 'javascripts/api/generated'
import { WorkPreferenceFormProps } from './types'
import { useAtom } from 'jotai'
import { stateAtom } from 'spa/member/atoms/WorkPreference/stateAtom'

export const WorkPreferenceForm: React.FC<WorkPreferenceFormProps> = (props: WorkPreferenceFormProps) => {
  const [_state, setState] = useAtom(stateAtom)

  const { ...method } = useForm({
    mode: 'onChange',
    defaultValues: async () => {
      const response = await API.getWorkPreference()
      const json: WorkPreference | null = response.data

      const WEEKDAYS = ['0', '1', '2', '3', '4', '5', '6']
      const workDaysAllSelected = WEEKDAYS.every(day =>
        json?.work_days_of_week?.includes(Number(day)) ?? false
      )
      const officeDaysAllSelected = WEEKDAYS.every(day =>
        json?.office_days_of_week?.includes(Number(day)) ?? false
      )

      const desiredMonthlySalary = json?.desired_monthly_salary || 600000
      const desiredHourlySalary = Math.ceil(desiredMonthlySalary / 160)
      const minWorkHoursPerMonth = json?.min_work_hours_per_week * 4
      const maxWorkHoursPerMonth = json?.max_work_hours_per_week * 4

      return {
        // FirstForm
        start_date: json?.start_date,
        min_work_hours_per_week: json?.min_work_hours_per_week,
        max_work_hours_per_week: json?.max_work_hours_per_week,
        _min_work_hours_per_month: minWorkHoursPerMonth,
        _max_work_hours_per_month: maxWorkHoursPerMonth,
        _min_desired_salary_range: (minWorkHoursPerMonth * desiredHourlySalary).toLocaleString(),
        _max_desired_salary_range: (maxWorkHoursPerMonth * desiredHourlySalary).toLocaleString(),
        work_days_of_week: workDaysAllSelected
          ? ['all', ...json.work_days_of_week.map(val => val.toString())]
          : json?.work_days_of_week?.map(val => val.toString()),
        office_days_of_week: json?.office_days_of_week?.length === 0
          ? ['7']
          : officeDaysAllSelected
          ? ['all', ...json.office_days_of_week.map(val => val.toString())]
          : json?.office_days_of_week?.map(val => val.toString()),
        remote_work_preference: json?.remote_work_preference,
        available_time: json?.available_time,
        // SecondForm
        desired_monthly_salary: desiredMonthlySalary,
        _desired_hourly_salary: Math.ceil(desiredMonthlySalary / 160).toLocaleString(),
        additional_conditions: json?.additional_conditions,
        full_time_job_introduction: json?.full_time_job_introduction?.toString(),
        temp_job_introduction: json?.temp_job_introduction?.toString(),
        pre_communication_notes: json?.pre_communication_notes,
        // FourthForm
        excluded_companies: json?.excluded_companies,
        work_from_abroad: json?.work_from_abroad?.toString(),
      }
    },
  })

  const onSubmit = async (data) => {
    setState(prev => ({...prev, sending: true }))

    const {
      _max_work_hours_per_month,
      _min_work_hours_per_month,
      _desired_hourly_salary,
      _min_desired_salary_range,
      _max_desired_salary_range,
      ...sendData
    } = data

    const formattedData = {
      ...sendData,
      work_days_of_week: sendData.work_days_of_week
        .filter(day => day !== 'all')
        .map(Number),
      office_days_of_week: sendData.office_days_of_week.length === 1 &&
      sendData.office_days_of_week[0] === '7'
        ? []
        : sendData.office_days_of_week
          .filter(day => day !== 'all')
          .map(Number),
      min_work_hours_per_week: Number(sendData.min_work_hours_per_week),
      max_work_hours_per_week: Number(sendData.max_work_hours_per_week),
      temp_job_introduction: sendData.temp_job_introduction === "true",
      full_time_job_introduction: sendData.full_time_job_introduction === "true",
      desired_monthly_salary: Number(sendData.desired_monthly_salary),
      work_from_abroad: sendData.work_from_abroad === "true",
      form_group: WorkPreferenceFormFormGroupEnum.AllFields,
    }

    try {
      await API.updateWorkPreference(formattedData)
    } catch (error) {
      window.alert('エラーが発生しました。')
      props.onError?.()
      return
    } finally {
      setState(prev => ({...prev, sending: false }))
    }

    // defaultValuesを再設定して、isDirtyの値をリセットする
    method.reset(data)

    props.onSubmit?.()
  }

  return (
    <FormProvider {...method}>
      <form id="profile-sheet-work-preference-form" onSubmit={method.handleSubmit(onSubmit)} noValidate>
        <StyledWorkPreferenceForm />
      </form>
    </FormProvider>
  )
}
